import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { adminApi } from "@/api/admin";
import type { ReteEvento } from "@/types/admin";
import { formatDateTime } from "@/lib/dates";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReteResponsabili } from "./ReteResponsabili";
import { ReteAgenzie } from "./ReteAgenzie";
import { ReteQuote } from "./ReteQuote";

const SCHEDE = [
  { valore: "responsabili", etichetta: "Responsabili" },
  { valore: "agenzie", etichetta: "Agenzie" },
  { valore: "registro", etichetta: "Registro" },
  { valore: "quote", etichetta: "Quote" },
] as const;
type Scheda = (typeof SCHEDE)[number]["valore"];

/** Scheda attiva dall'URL (`?tab=`): un link alla scheda Agenzie resta sulla scheda Agenzie. */
function schedaDaUrl(sp: URLSearchParams): Scheda {
  const t = sp.get("tab");
  return SCHEDE.some((s) => s.valore === t) ? (t as Scheda) : "responsabili";
}

export function Rete() {
  const [sp, setSp] = useSearchParams();
  const scheda = schedaDaUrl(sp);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Rete</h2>
        <p className="text-sm text-muted-foreground">Chi ha in carico quali agenzie</p>
      </div>
      <Tabs
        value={scheda}
        onValueChange={(v) =>
          setSp((prev) => {
            prev.set("tab", String(v));
            return prev;
          }, { replace: true })
        }
      >
        {/* Stesse classi di Affiliazioni: hover:text-primary sull'attivo, altrimenti
            l'hover del trigger base vince sul verde. */}
        <TabsList className="h-auto rounded-lg border border-border bg-card p-1 shadow-sm">
          {SCHEDE.map((s) => (
            <TabsTrigger
              key={s.valore}
              value={s.valore}
              className="rounded-md px-4 py-1.5 data-active:bg-primary/10 data-active:text-primary data-active:hover:text-primary"
            >
              {s.etichetta}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="responsabili" className="pt-2"><ReteResponsabili /></TabsContent>
        <TabsContent value="agenzie" className="pt-2"><ReteAgenzie /></TabsContent>
        <TabsContent value="registro" className="pt-2"><ReteRegistro /></TabsContent>
        <TabsContent value="quote" className="pt-2"><ReteQuote /></TabsContent>
      </Tabs>
    </div>
  );
}

/** Registro di `rete_eventi`: chi ha fatto cosa, in ordine dal più recente. */
function ReteRegistro() {
  const [eventi, setEventi] = useState<ReteEvento[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi.reteEventi()
      .then(setEventi)
      .catch(() => setError("Impossibile caricare il registro."));
  }, []);

  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (eventi === null) return <p className="text-sm text-muted-foreground">Caricamento...</p>;
  if (eventi.length === 0) return <p className="text-sm text-muted-foreground">Nessun evento registrato.</p>;

  return (
    <section className="rounded-lg border border-border bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Azione</TableHead>
              <TableHead className="hidden md:table-cell">Chi</TableHead>
              <TableHead>Dettaglio</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {eventi.map((e) => (
              <TableRow key={e.id} className="align-top">
                <TableCell className="whitespace-nowrap tabular-nums text-muted-foreground">
                  {formatDateTime(e.created_at)}
                </TableCell>
                <TableCell className="text-foreground">{e.azione.replace(/_/g, " ")}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{e.actor ?? "—"}</TableCell>
                <TableCell className="text-xs font-mono text-muted-foreground break-all">
                  {e.evidence ? JSON.stringify(e.evidence) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
